import { useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import {
    startReencryption,
    submitReencryptionChunk,
    getPendingBatch,
} from "~/services/gateways/orgEncryption.gateway";
import { rsaOaepUnwrapDek, importRsaPrivateKey } from "~/helpers/orgCrypto";
import { wrapDekForUser, base64ToBuffer } from "~/helpers/envelopeCrypto";
import type { PendingBatchResponse } from "~/domain/OrgEncryption";
import type { WsItemsMessage } from "~/hooks/useReencryptionWs";

export interface ReencryptionProgress {
    total: number;
    completed: number;
    failed: number;
    isRunning: boolean;
}

/**
 * Streaming re-encryption hook.
 *
 * Uses a single bidirectional WebSocket connection:
 * - Server pushes pages of 100 items (only the org_wrapped_dek, not report data)
 * - Frontend re-wraps each DEK with the new user key (fast — no data decryption)
 * - Frontend POSTs the chunk, then asks the WS for the next page
 * - A persistent sonner toast shows progress even after the dialog is closed
 */
export function useReencryption() {
    const [progress, setProgress] = useState<ReencryptionProgress>({
        total: 0, completed: 0, failed: 0, isRunning: false,
    });
    const [activeBatchId, setActiveBatchId] = useState<string | null>(null);

    const abortRef = useRef(false);
    const toastIdRef = useRef<string | number | null>(null);
    const opkRef = useRef<CryptoKey | null>(null);
    const userDekRef = useRef<ArrayBuffer | null>(null);

    /** Update the persistent toast with current progress */
    const updateToast = useCallback((completed: number, total: number, failed: number, done: boolean) => {
        if (!toastIdRef.current) {
            toastIdRef.current = toast.loading("Re-criptografando relatórios...", {
                description: `0/${total} concluídos`,
                duration: Infinity,
            });
        }

        if (done) {
            if (failed > 0) {
                toast.warning("Re-criptografia finalizada com erros", {
                    id: toastIdRef.current,
                    description: `${completed} concluído(s), ${failed} falha(s)`,
                    duration: 8000,
                });
            } else {
                toast.success("Re-criptografia concluída", {
                    id: toastIdRef.current,
                    description: `${completed}/${total} itens re-criptografados`,
                    duration: 5000,
                });
            }
            toastIdRef.current = null;
        } else {
            toast.loading("Re-criptografando relatórios...", {
                id: toastIdRef.current,
                description: `${completed}/${total} concluídos${failed > 0 ? ` (${failed} falha(s))` : ""}`,
                duration: Infinity,
            });
        }
    }, []);

    /**
     * Process a page of items received from the WS.
     * Re-wraps each DEK and POSTs the chunk. Returns the count of successes/failures.
     */
    const processItems = useCallback(async (
        items: WsItemsMessage["items"],
        batchId: string,
    ): Promise<{ ok: number; fail: number }> => {
        const opk = opkRef.current;
        const userDek = userDekRef.current;
        if (!opk || !userDek) return { ok: 0, fail: items.length };

        const chunkItems: Array<{
            report_id: string;
            new_user_wrapped_dek: string;
            new_user_dek_iv: string;
            item_type: string;
        }> = [];
        let chunkFailed = 0;

        for (const entry of items) {
            if (abortRef.current) break;
            try {
                const wrappedDekBuffer = base64ToBuffer(entry.org_wrapped_dek);
                const ab = new ArrayBuffer(wrappedDekBuffer.byteLength);
                new Uint8Array(ab).set(wrappedDekBuffer);

                const perItemDek = await rsaOaepUnwrapDek(opk, ab);
                const { user_wrapped_dek, user_dek_iv } = await wrapDekForUser(userDek, perItemDek);

                chunkItems.push({
                    report_id: entry.item_id,
                    new_user_wrapped_dek: user_wrapped_dek,
                    new_user_dek_iv: user_dek_iv,
                    item_type: entry.item_type,
                });
            } catch (err) {
                console.error(`[useReencryption] Failed ${entry.item_type} ${entry.item_id}:`, err);
                chunkFailed++;
            }
        }

        if (chunkItems.length > 0) {
            try {
                await submitReencryptionChunk({ batch_id: batchId, items: chunkItems });
                return { ok: chunkItems.length, fail: chunkFailed };
            } catch (err) {
                console.error("[useReencryption] Chunk submission failed:", err);
                return { ok: 0, fail: chunkItems.length + chunkFailed };
            }
        }

        return { ok: 0, fail: chunkFailed };
    }, []);

    /** Called by the WS hook when a page of items arrives */
    const handleItemsMessage = useCallback(async (
        msg: WsItemsMessage,
        requestNextPage: () => void,
        requestProgress: () => void,
    ) => {
        if (abortRef.current || msg.items.length === 0) {
            setProgress(prev => ({ ...prev, isRunning: false }));
            updateToast(msg.completed, msg.total, msg.failed, true);
            requestProgress(); // Ask server to confirm final status
            return;
        }

        const { ok, fail } = await processItems(msg.items, msg.batch_id);

        const newCompleted = msg.completed + ok;
        const newFailed = msg.failed + fail;

        setProgress({ total: msg.total, completed: newCompleted, failed: newFailed, isRunning: msg.has_more });
        updateToast(newCompleted, msg.total, newFailed, !msg.has_more);

        if (msg.has_more && !abortRef.current) {
            requestNextPage();
        } else {
            setProgress(prev => ({ ...prev, isRunning: false }));
            requestProgress(); // Ask server to confirm batch is complete → triggers { type: "complete" }
        }
    }, [processItems, updateToast]);

    /** Start a brand-new re-encryption flow */
    const startReencryptionFlow = useCallback(async (
        targetUserId: string,
        newSalt: string,
        newVerifier: Record<string, unknown>,
        opkBytes: ArrayBuffer,
        newUserDek: ArrayBuffer,
    ) => {
        abortRef.current = false;
        setProgress({ total: 0, completed: 0, failed: 0, isRunning: true });

        const result = await startReencryption({
            target_user_id: targetUserId,
            new_salt: newSalt,
            new_verifier: newVerifier,
        });

        const { batch_id, total_count } = result;
        setActiveBatchId(batch_id);
        setProgress({ total: total_count, completed: 0, failed: 0, isRunning: true });

        if (total_count === 0) {
            setProgress({ total: 0, completed: 0, failed: 0, isRunning: false });
            return batch_id;
        }

        // Import OPK once and store in ref for processItems
        try {
            opkRef.current = await importRsaPrivateKey(opkBytes);
        } catch (err) {
            console.error("[useReencryption] Failed to import OPK:", err);
            setProgress(prev => ({ ...prev, isRunning: false, failed: prev.total }));
            throw new Error("Falha ao importar a chave privada da organização.");
        }
        userDekRef.current = newUserDek;

        // Toast will be created when first items arrive via WS
        return batch_id;
    }, []);

    /** Resume an existing in-progress batch */
    const resumeReencryptionFlow = useCallback(async (
        pendingBatch: PendingBatchResponse,
        opkBytes: ArrayBuffer,
        newUserDek: ArrayBuffer,
    ) => {
        abortRef.current = false;

        const { batch_id, total_count, completed_count, failed_count } = pendingBatch;
        setActiveBatchId(batch_id);
        setProgress({ total: total_count, completed: completed_count, failed: failed_count, isRunning: true });

        if (completed_count + failed_count >= total_count) {
            setProgress(prev => ({ ...prev, isRunning: false }));
            return batch_id;
        }

        try {
            opkRef.current = await importRsaPrivateKey(opkBytes);
        } catch (err) {
            console.error("[useReencryption] Failed to import OPK on resume:", err);
            setProgress(prev => ({ ...prev, isRunning: false }));
            throw new Error("Falha ao importar a chave privada da organização.");
        }
        userDekRef.current = newUserDek;

        return batch_id;
    }, []);

    const abort = useCallback(() => { abortRef.current = true; }, []);

    return {
        startReencryptionFlow,
        resumeReencryptionFlow,
        handleItemsMessage,
        abort,
        progress,
        activeBatchId,
    };
}

/** Check if there is a resumable batch for the given target user. */
export async function checkResumableBatch(targetUserId: string): Promise<PendingBatchResponse | null> {
    try {
        return await getPendingBatch(targetUserId);
    } catch {
        return null;
    }
}
